import { Colors } from '@be-brite/briteui';
import { BookOpenIcon, CircleWavyQuestionIcon, ThumbsDownIcon } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { Box, Loader, Text } from '../shared-components';
import { useAssistantChat } from './context/AssistantChatProvider';
import { AssistantChatAssistantResponse } from './AssistantChatAssistantResponse';
import { AssistantChatUserMessage } from './AssistantChatUserMessage';

export const AssistantChatThread = () => {
  // Importing the context to access the chat thread and loading state.
  const { thread, isLoading } = useAssistantChat();

  // Ref to scroll to the end of the chat history.
  const chatEndRef = useRef(null);

  // Effect to scroll to the end of the chat history whenever it changes.
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread]);

  if (isLoading) {
    return (
      <Box
        css={`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        `}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        gap: 16px;
      `}
    >
      {thread?.length === 0 ? (
        <EmptyState />
      ) : (
        thread?.map((message, index) => (
          <Box
            key={index}
            css={`
              display: flex;
              justify-content: ${message.sender === 'user' ? 'flex-end' : 'flex-start'};
            `}
          >
            {message.sender === 'user' ? (
              <AssistantChatUserMessage message={message} />
            ) : (
              <AssistantChatAssistantResponse message={message} />
            )}
          </Box>
        ))
      )}
      <Box
        ref={chatEndRef}
        css={`
          padding-bottom: 12px;
        `}
      />
    </Box>
  );
};

const EmptyState = () => {
  // Empty state item component.
  const EmptyStateItem = ({ icon: Icon, color, text }) => (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        padding: 12px 16px;
        gap: 12px;
      `}
    >
      <Box
        css={`
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 12px;
          width: 52px;
          height: 52px;
          border-radius: 8px;
          background: color-mix(in srgb, ${color} 10%, transparent);

          @media (max-width: 768px) {
            width: 44px;
            height: 44px;
          }
        `}
      >
        <Icon size={36} color={color} />
      </Box>
      <Text caption center color={Colors.Gray600}>
        {text}
      </Text>
    </Box>
  );

  return (
    <Box
      css={`
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 12px;
        margin-top: 16px;
        border-radius: 8px;
        border: 1px solid ${Colors.Gray200};
        background: ${Colors.White};
      `}
    >
      <Box
        css={`
          display: flex;
          gap: 8px;
        `}
      >
        <EmptyStateItem
          icon={CircleWavyQuestionIcon}
          color={Colors.LiteRadical}
          text="Ask a benefits related question"
        />
        <EmptyStateItem
          icon={BookOpenIcon}
          color={Colors.LitePurple}
          text="Answers are based on content in this guide"
        />
        <EmptyStateItem icon={ThumbsDownIcon} color={Colors.LiteTango} text="Report answers that seem incorrect" />
      </Box>
    </Box>
  );
};
